import { T, useT } from '@transifex/react';
import { Fragment } from 'react/jsx-runtime';
import { useFormContext } from 'react-hook-form';

import spacing from 'pkg/config/spacing';

import Link from 'pkg/router/Link';

import MaterialSymbol from 'components/material-symbols';
import SectionTitle from 'components/section_title';

import * as Input from 'components/form/inputs';
import Column from 'components/layout/column';
import Row from 'components/layout/row';

import Button from 'design/button';

import * as css from './styles.css';

interface FormItemsProps {
	isSubmitting: boolean;
	toggleForm: () => void;
}

export function FormItems({ isSubmitting, toggleForm }: FormItemsProps) {
	const t = useT();
	const formCtx = useFormContext();
	const createAccount = formCtx.watch('createAccount');
	const email = formCtx.watch('email');
	const confirmEmail = formCtx.watch('email-confirm');

	const day = formCtx.watch('day');
	const month = formCtx.watch('month');
	const year = formCtx.watch('year');

	const handleDatePartChange = () => {
		const y = parseInt(formCtx.getValues('year'), 10);
		const m = parseInt(formCtx.getValues('month'), 10);
		const d = parseInt(formCtx.getValues('day'), 10);

		if (y && m && d) {
			const mm = String(m).padStart(2, '0');
			const dd = String(d).padStart(2, '0');
			formCtx.setValue('dateOfBirth', `${y}-${mm}-${dd}`);
		}
	};

	const validateEmail = () => {
		if (email !== confirmEmail) {
			return false;
		}

		return true;
	};

	return (
		<div className={css.body}>
			<Column spacing={spacing._6}>
				<Column>
					<Row align="center">
						<SectionTitle>{t('Add new child')}</SectionTitle>
						<div className={css.close} onClick={toggleForm}>
							<MaterialSymbol actualSize scale={1.4} variant="close" />
						</div>
					</Row>
					<Input.Group label={t('First name')}>
						<Input.Field name="firstName" required />
					</Input.Group>
					<Input.Group label={t('Last name')}>
						<Input.Field name="lastName" required />
					</Input.Group>
					<Input.BirthDate
						label={t(`Child's date of birth`)}
						year={year}
						month={month}
						day={day}
						onChange={handleDatePartChange}
					/>
					<Input.Control
						name="createAccount"
						type="checkbox"
						label={t('Child should be able to access their own account')}
					/>

					{createAccount && (
						<Fragment>
							<span className={css.collectEmail}>
								{t(
									`We will need to collect a unique email address for your child. If you don't create a login for your child, you will still be able to manage team related activities on their behalf.`
								)}
							</span>
							<Input.Group label={t(`Child's email`)}>
								<Input.Field name="email" validate={validateEmail} required />
							</Input.Group>
							<Input.Group label={t(`Confirm child's email`)}>
								<Input.Field
									name="email-confirm"
									validate={validateEmail}
									required
								/>
							</Input.Group>
							<Input.CreatePassword />
						</Fragment>
					)}
				</Column>

				<Column>
					<span className={css.terms}>
						<T
							_str="By submitting this you agree to our {terms_and_conditions_link}."
							terms_and_conditions_link={
								<Link href="https://www.360player.com/terms-of-service">
									{t('terms and conditions')}
								</Link>
							}
						/>
					</span>

					<Button
						type="submit"
						primary
						label={t('Add child')}
						isLoading={isSubmitting}
					/>
				</Column>
			</Column>
		</div>
	);
}
